import React from "react";
import { ReactNode, useState } from "react";
import { Card, Theme } from "../components/cards";
import CircleImage from "../components/circleImage";
import { FlexColumn, FlexRow } from "../components/flex";
import { Socials } from "../components/socials";
import {
  ThemedGradientSpan,
  H2,
  H3,
  H4,
  LinkSpan,
  P,
  Title,
  GradientSpan,
  lightGradient,
} from "../components/typography";
import { useIsMobileLayout } from "../hooks/useIsMobileLayout";

export function Home() {
  return (
    <div className="App">
      <HeaderSection />
      <ExperienceSection />
      <ProjectSection />
      <BlogSection/>
      <WorkWithMeSection />
    </div>
  );
}

const HeaderSection = () => {
  const headerSpacing = 100;
  const isMobileLayout = useIsMobileLayout();
  return (
    <SectionWrapper theme="dark" spacing={headerSpacing}>
      <FlexRow>
        {isMobileLayout ? null : (
          <CircleImage
            src="./profile_square.png"
            alt="A picture of Eric Tu"
            size={160}
          />
        )}
        <FlexColumn
          style={{
            marginLeft: isMobileLayout ? 0 : 60,
            justifyContent: "center",
          }}
        >
          <Title style={{ textAlign: "start", marginBottom: 16 }}>
            Hi, I'm Eric 👋
          </Title>
          <H3
            style={{ maxWidth: isMobileLayout ? 340 : 368, textAlign: "start" , marginBottom: 32}}
          >
            a{" "}
            <ThemedGradientSpan theme="light">
              Front-End & Full-Stack Engineer
            </ThemedGradientSpan>{" "}
            based in Brooklyn, NYC 🗽
          </H3>
          <Socials/>
        </FlexColumn>
      </FlexRow>

      <FlexRow
        style={{
          marginTop: 32,
          width: isMobileLayout ? 280 : undefined,
        }}
      >
        <P>
          this page was built{" "}
          <LinkSpan url="https://github.com/erictu22/erictu22.github.io">
            from scratch
          </LinkSpan>{" "}
          using React.js
        </P>
      </FlexRow>
    </SectionWrapper>
  );
};

const ExperienceSection = () => {
  const spacing = 120;
  const cardTheme: Theme = "dark";
  const isMobileLayout = useIsMobileLayout();
  return (
    <SectionWrapper theme="light" spacing={spacing}>
      <H2>Experience</H2>
      <FlexColumn style={{ width: isMobileLayout ? undefined : 700 }}>
        <FlexRow style={{ marginTop: spacing / 2 }} wrap={isMobileLayout}>
          <Card theme={cardTheme} url="https://apps.apple.com/app/id6447978247">
            <H3 align="start">Convo</H3>
            <P align="start" bold style={{ marginTop: 4, marginBottom: 4 }}>
              Founder
            </P>
            <P align="start">
              A mobile app that acts as your personal AI language coach
            </P>
          </Card>
          <Card theme={cardTheme} url="https://www.shortwave.com/">
            <H3 align="start">Shortwave</H3>
            <P align="start" bold style={{ marginTop: 4, marginBottom: 4 }}>
              Full-Stack Engineer
            </P>
            <P align="start">
              Worked with ex-Googlers & the previous founders of Firebase to
              build an AI-powered email client
            </P>
          </Card>
        </FlexRow>
        <FlexRow wrap={isMobileLayout}>
          <Card theme={cardTheme} url="https://dminc.com/">
            <H3 align="start">DMI</H3>
            <P align="start" bold style={{ marginTop: 4, marginBottom: 4 }}>
              Software Engineer
            </P>
            <P align="start">Developed data population scripts for AT&T</P>
          </Card>
          <Card theme={cardTheme} url="https://www.rtx.com/">
            <H3 align="start">Raytheon</H3>
            <P align="start" bold style={{ marginTop: 4, marginBottom: 4 }}>
              Software Engineer Intern
            </P>
            <P align="start">
              Developed memory back-up drivers used in the Boeing 777x
            </P>
          </Card>
        </FlexRow>
      </FlexColumn>
    </SectionWrapper>
  );
};

const ProjectSection = () => {
  const spacing = 70;
  const cardTheme: Theme = "light";
  const isMobileLayout = useIsMobileLayout();

  return (
    <SectionWrapper theme="dark" spacing={spacing}>
      <H2>Projects</H2>
      <FlexColumn style={{ width: isMobileLayout ? undefined : 700 }}>
        <FlexRow
          style={{ marginTop: spacing / 2, overflow: "auto" }}
          wrap={isMobileLayout}
        >
          <Card
            theme={cardTheme}
            url="https://www.linkedin.com/posts/activity-7062608380481138688-xou1?utm_source=share&utm_medium=member_desktop"
          >
            <H3 align="start">Automated video editor</H3>
            <P align="start">
              a suite of Python scripts that uses image recognition to edit
              YouTube videos
            </P>
          </Card>
          <Card
            theme={cardTheme}
            url="https://apps.apple.com/ma/app/pictojump/id1613057956"
          >
            <H3 align="start">PictoJump</H3>
            <P align="start">
              an iOS mobile game that turns your phone's photo library into a
              platformer game
            </P>
          </Card>
        </FlexRow>
        <FlexRow wrap={isMobileLayout}>
          <Card theme={cardTheme} url="https://devpost.com/software/puppet-io">
            <H3 align="start">Puppet.io</H3>
            <P align="start">
              an image recognition game that won the 2021 MakeHarvard
              Hack-a-thon
            </P>
          </Card>
        </FlexRow>
      </FlexColumn>
    </SectionWrapper>
  );
};

const BlogSection = () => {
  const isMobileLayout = useIsMobileLayout();
  const cardTheme: Theme = "dark";
  const spacing = 120;
  return <SectionWrapper theme="light" spacing={spacing}>
  <H2>Blog</H2>
  <FlexColumn style={{ width: isMobileLayout ? undefined : 700 }}>
    <FlexRow
      style={{ marginTop: spacing / 2, overflow: "auto" }}
      wrap={isMobileLayout}
    >
      <Card
        theme={cardTheme}
        url="https://erictu.substack.com/p/i-hate-the-term-prompt-engineering"
      >
        <H3 align="start">Less is more</H3>
        <P align="start">
          How shorter prompts lead to more robust and coherent responses
        </P>
      </Card>
      <Card
        theme={cardTheme}
        url="https://erictu.substack.com/p/system-architecture-interview-tips"
      >
        <H3 align="start">My favorite design pattern</H3>
        <P align="start">
          The singleton pattern is simple and beautiful, but it has its trade-offs
        </P>
      </Card>
    </FlexRow>
  </FlexColumn>
</SectionWrapper>
}

const WorkWithMeSection = () => {
  const spacing = 120;
  return (
    <SectionWrapper theme="dark" spacing={spacing}>
      <FlexColumn style={{alignItems: 'center', gap: 20}}>
      <P>"If you have an idea that you genuinely think is good, don't let some idiot talk you out of it."</P>
      <P>"Whatever you do, give it your best shot and you'll be glad you did."</P>
      <P align="start" style={{ marginBottom: 40 }}>
        - Stan Lee - 
      </P>
      </FlexColumn>
    </SectionWrapper>
  );
};

const SectionWrapper: React.FC<{
  children?: ReactNode;
  theme: Theme;
  spacing: number;
}> = (props) => {
  return (
    <FlexColumn
      style={{
        alignItems: "center",
        paddingTop: props.spacing,
        paddingBottom: props.spacing,
        backgroundColor: props.theme === "light" ? "white" : "#303045",
        color: props.theme === "light" ? "#303045" : "white",
      }}
    >
      {props.children}
    </FlexColumn>
  );
};
